if (!customElements.get('aplovio-navigation')) {
  customElements.define('aplovio-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.contentAnimations = new Set();
      const options = {signal:this.abort.signal};
      this.layer = this.closest('aplovio-layer');
      this.dialog = this.closest('dialog');
      this.header = this.closest('aplovio-header');
      this.stage = this.querySelector('[data-nav-stage]');
      this.featureStage = this.querySelector('[data-nav-feature-stage]');
      this.scroll = this.querySelector('.ap-nav-scroll');
      this.root = this.querySelector('[data-nav-root]');
      this.rootId = this.root.id;
      this.panes = new Map();
      this.current = this.rootId;
      this.trail = [];
      this.nativeMenuHTML = this.root.querySelector('[data-nav-root-content]').innerHTML;
      this.menuRevision = 0;
      this.registerPanes();
      this.registerFeatures();
      this.searchEntry = this.querySelector('[data-nav-search]');
      this.searchEnabled = this.searchEntry && !this.searchEntry.hidden;
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-nav-target]');
        if (trigger) {event.preventDefault(); this.activate(trigger.dataset.navTarget,trigger);}
        if (event.target.closest('[data-nav-back]')) {event.preventDefault(); this.back();}
        const next = event.target.closest('[data-nav-next]');
        if (next) {
          const track = next.parentElement.querySelector('[data-nav-carousel]');
          const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
          track.scrollBy({left:direction * track.clientWidth * .65,behavior:this.duration() ? 'smooth' : 'instant'});
        }
      }, options);
      this.layer.addEventListener('aplovio:layer-open', event => {
        if (event.target !== this.dialog) return;
        this.dialog.classList.remove('ap-nav-closing');
        this.show(this.current,false);
        if (!this.sheet()) this.enterContent(true);
      }, options);
      this.layer.addEventListener('aplovio:layer-beforeclose', event => {
        if (event.target !== this.dialog || this.finishing || event.defaultPrevented || this.sheet() || !this.duration('close')) return;
        event.preventDefault();
        if (this.closing) return;
        this.closing = true;
        const primary = this.querySelector('.ap-nav-primary');
        primary.style.setProperty('--ap-nav-exit-transform',getComputedStyle(primary).transform);
        primary.style.setProperty('--ap-nav-exit-clip',getComputedStyle(primary).clipPath);
        const activePane = this.panes.get(this.current)?.node;
        const fadeTargets = [...new Set([...this.querySelector('.ap-nav-closebar').children,this.searchEntry,activePane?.querySelector('.ap-nav-submenu-header'),...activePane.querySelectorAll('.ap-nav-link'),...this.querySelectorAll('.ap-nav-footer :is(.ap-nav-link,.ap-nav-utility)')].filter(Boolean))];
        const fades = fadeTargets.map(node => ({node,opacity:getComputedStyle(node).opacity,transform:getComputedStyle(node).transform}));
        this.clearContentMotion();
        for (const {node,opacity,transform} of fades) this.animateContent(node,[{opacity,transform},{opacity:0,transform}],this.duration('close') * .55,0,true);
        this.dialog.classList.add('ap-nav-closing');
        this.closeTimer = setTimeout(() => {
          this.finishing = true;
          this.layer.close(event.detail.reason);
          this.finishing = false;
        }, this.duration('close'));
      }, options);
      this.layer.addEventListener('aplovio:layer-close', event => {
        if (event.target !== this.dialog) return;
        clearTimeout(this.closeTimer); this.closing = false;
        this.dialog.classList.remove('ap-nav-closing');
        this.trail = []; this.show(this.rootId,false);
        // The shared layer restores the actual opener, including bottom navigation.
      }, options);
      this.header?.addEventListener('click', event => {
        if (!this.dialog.open || !event.target.closest(`[data-layer-open="${this.layer.id}"]`)) return;
        event.preventDefault(); event.stopImmediatePropagation(); this.layer.close('button');
      }, {...options,capture:true});
      const settleMotion = () => {if(this.duration())return;this.clearContentMotion();if(this.closing){clearTimeout(this.closeTimer);this.finishing=true;this.layer.close('motion');this.finishing=false;}};
      matchMedia('(prefers-reduced-motion:reduce)').addEventListener('change',settleMotion,options);
      this.motionObserver = new MutationObserver(settleMotion);
      this.motionObserver.observe(this.closest('.ap-nav-owner'),{attributes:true,attributeFilter:['data-motion']});
      window.addEventListener('resize', () => {if (this.dialog.open) this.placeFeatured();}, options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.header?.contains(event.target)) return;
        const node = event.target;
        const feature = [...this.featureHomes.keys()].find(item => item === node || item.contains(node));
        const direct = node.matches('[data-nav-target]') ? node : node.querySelector(':scope > [data-nav-target]');
        const pane = [...this.panes.values()].find(entry => entry.node === node || entry.node.contains(node) || entry.feature?.contains(node));
        if (!pane && !direct && !feature && !this.contains(node) && !node.contains(this)) return;
        this.layer.open(this.header.querySelector(`[data-layer-open="${this.layer.id}"]`));
        this.trail = [];
        if (direct && this.panes.has(direct.dataset.navTarget)) this.show(direct.dataset.navTarget,false);
        else if (pane) this.show(pane.node.id,false);
        else {this.show(this.rootId,false); if (feature) {this.previewFeature = feature; this.placeFeatured();}}
      }, options);
      this.dispatchEvent(new CustomEvent('aplovio:navigation-ready',{bubbles:true}));
    }
    sheet() {
      // A bottom drawer opens and closes with the shared drawer motion, content and all, like the bag.
      return getComputedStyle(this.dialog).getPropertyValue('--ap-layer-presentation').trim() === 'bottom';
    }
    duration(kind='open') {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || this.closest('.ap-nav-owner')?.dataset.motion === 'off') return 0;
      const value = getComputedStyle(this).getPropertyValue(kind==='close'?'--ap-layer-close-motion':'--ap-layer-open-motion').trim();
      return Math.min(1600,Math.max(0,parseFloat(value) * (value.endsWith('ms') ? 1 : 1000))) || 0;
    }
    animateContent(node,frames,duration,delay=0,hold=false) {
      if (!duration || !node || !this.dialog.open) return;
      const animation = node.animate(frames,{duration,delay,easing:getComputedStyle(this).getPropertyValue('--ap-ease').trim() || 'ease-out',fill:'both'});
      this.contentAnimations.add(animation);
      if (!hold) animation.finished.then(() => {this.contentAnimations.delete(animation);animation.cancel();},() => {});
      return animation;
    }
    clearContentMotion() {
      for (const animation of this.contentAnimations || []) animation.cancel();
      this.contentAnimations?.clear();
      if (this.leavingPane) {
        this.leavingPane.hidden = true;
        this.leavingPane.inert = false;
        this.leavingPane.removeAttribute('aria-hidden');
        this.leavingPane.classList.remove('ap-nav-pane-leaving');
        this.leavingPane.style.removeProperty('--ap-nav-leaving-top');
        this.leavingPane = null;
      }
    }
    enterContent(opening=false,direction='forward',delay=0) {
      const duration = this.duration(),pane = this.panes.get(this.current)?.node;
      if (!duration || !pane || !this.dialog.open) return;
      const stagger = duration / 7,entryDuration = duration * .9;
      const rise = [{opacity:0,transform:'translateY(10px)'},{opacity:1,transform:'translateY(0)'}];
      const header = pane.querySelector('.ap-nav-submenu-header');
      const sign = (direction === 'back' ? -1 : 1) * (getComputedStyle(this).direction === 'rtl' ? -1 : 1);
      this.animateContent(header,[{opacity:0,transform:`translateX(${sign * 12}px)`},{opacity:1,transform:'translateX(0)'}],entryDuration,delay);
      const links = [...pane.querySelectorAll('.ap-nav-link')].filter(node => node.closest('[data-nav-pane]') === pane);
      const start = opening ? duration * .75 : delay + (header ? stagger * 2 : 0);
      links.forEach((node,index) => this.animateContent(node,rise,entryDuration,start + Math.min(index,6) * stagger));
      if (opening) {
        const chrome = [...this.querySelector('.ap-nav-closebar').children,this.searchEntry,...this.querySelectorAll('.ap-nav-footer :is(.ap-nav-link,.ap-nav-utility)')].filter(Boolean);
        chrome.forEach((node,index) => this.animateContent(node,rise,entryDuration,duration * .55 + Math.min(index,5) * stagger));
      }
    }
    registerPanes() {
      const entries = [...this.querySelectorAll('[data-nav-pane]')].map(node => {
        const parent = node.parentElement.closest('[data-nav-pane]');
        const feature = node.querySelector(':scope > [data-nav-featured-content]');
        const placeholder = document.createComment('navigation pane');
        if (node !== this.root) node.before(placeholder);
        const featurePlaceholder = document.createComment('featured content');
        feature?.before(featurePlaceholder);
        return {node,parent:parent?.id || this.rootId,placeholder,feature,featurePlaceholder};
      });
      for (const entry of entries) {
        this.panes.set(entry.node.id,entry);
        if (entry.node !== this.root) this.stage.append(entry.node);
      }

    }
    destination(value) {
      if (!value || value === '#') return null;
      try {
        const url = new URL(value,location.origin);
        if (!['http:','https:'].includes(url.protocol)) return null;
        const root = (this.dataset.rootUrl || '/').replace(/\/$/,'');
        if (url.origin === location.origin && root && (url.pathname === root || url.pathname.startsWith(`${root}/`))) url.pathname = url.pathname.slice(root.length) || '/';
        url.pathname = url.pathname.replace(/\/$/,'') || '/';
        return url.href;
      } catch {return null;}
    }
    registerFeatures() {
      this.featureHomes = new Map();
      const destinations = new Map();
      for (const feature of this.querySelectorAll('[data-nav-feature-for]')) {
        const home = document.createComment('right panel home'); feature.before(home);
        this.featureHomes.set(feature,home);
        const destination = this.destination(feature.dataset.navFeatureFor);
        if (feature.dataset.navFeaturePlacement === 'inline') {
          for (const entry of this.panes.values()) if (destination && destination === this.destination(entry.node.dataset.navDestination)) (entry.inlineFeatures ||= []).push(feature);
        } else if (destination && !destinations.has(destination)) destinations.set(destination,feature);
      }
      for (const entry of this.panes.values()) {
        const feature = destinations.get(this.destination(entry.node.dataset.navDestination));
        if (!feature) continue;
        entry.feature = feature;
        entry.featurePlaceholder = document.createComment('right panel position');
        entry.node.append(entry.featurePlaceholder);
      }
    }
    activate(id,trigger) {
      if (!this.panes.has(id) || this.current === id) return;
      this.menuRevision++;
      this.trail.push({id:this.current,trigger});
      trigger.setAttribute('aria-expanded','true');
      this.show(id,true,'forward');
      this.panes.get(id).node.querySelector('[data-nav-back]')?.focus({preventScroll:true});
    }
    back() {
      this.menuRevision++;
      const previous = this.trail.pop();
      const id = previous?.id || this.panes.get(this.current)?.parent || this.rootId;
      this.show(id,true,'back');
      previous?.trigger?.setAttribute('aria-expanded','false');
      previous?.trigger?.focus({preventScroll:true});
    }
    show(id,animate,direction='forward') {
      if (!this.panes.has(id)) id = this.rootId;
      const previous = this.panes.get(this.current);
      const outgoing = animate && previous?.node.id !== id && this.dialog.open && this.duration() ? previous?.node : null;
      const outgoingOpacity = outgoing ? getComputedStyle(outgoing).opacity : 1;
      const outgoingContent = outgoing ? [...outgoing.querySelectorAll('.ap-nav-submenu-header,.ap-nav-link')].map(node => ({node,opacity:getComputedStyle(node).opacity,transform:getComputedStyle(node).transform})) : [];
      const outgoingScroll = this.scroll.scrollTop;
      this.clearContentMotion();
      if (previous?.feature && previous.featurePlaceholder.parentNode) previous.featurePlaceholder.after(previous.feature);
      for (const [feature,home] of this.featureHomes || []) if (home.parentNode) home.after(feature);
      this.previewFeature = null;
      this.current = id;
      if (this.searchEntry) this.searchEntry.hidden = !this.searchEnabled || (id !== this.rootId && this.searchEntry.dataset.submenus === 'false');
      this.querySelectorAll('[data-nav-target]').forEach(trigger => trigger.setAttribute('aria-expanded',String(trigger.dataset.navTarget === id)));
      for (const entry of this.panes.values()) {
        if (entry.node.id === id) entry.node.dataset.navDirection = direction;
        entry.node.hidden = entry.node.id !== id;
      }
      this.scroll.scrollTop = 0;
      this.placeFeatured();
      if (outgoing) {
        outgoing.hidden = false;outgoing.inert = true;outgoing.setAttribute('aria-hidden','true');outgoing.classList.add('ap-nav-pane-leaving');
        outgoing.style.setProperty('--ap-nav-leaving-top',`${-outgoingScroll}px`);
        this.leavingPane = outgoing;
        const sign = (direction === 'back' ? 1 : -1) * (getComputedStyle(this).direction === 'rtl' ? -1 : 1);
        const exitDuration = this.duration('close') * .6;
        const held = outgoingContent.map(({node,opacity,transform}) => this.animateContent(node,[{opacity,transform},{opacity,transform}],exitDuration,0,true));
        const exit = this.animateContent(outgoing,[{opacity:outgoingOpacity,transform:'translateX(0)'},{opacity:0,transform:`translateX(${sign * 10}px)`}],exitDuration);
        exit?.finished.then(() => {for(const animation of held){animation?.cancel();this.contentAnimations.delete(animation);}if(this.leavingPane === outgoing){outgoing.hidden=true;outgoing.inert=false;outgoing.removeAttribute('aria-hidden');outgoing.classList.remove('ap-nav-pane-leaving');outgoing.style.removeProperty('--ap-nav-leaving-top');this.leavingPane=null;}},() => {});
        this.enterContent(false,direction,exitDuration * .65);
      }
    }
    placeFeatured() {
      const entry = this.panes.get(this.current);
      const feature = this.previewFeature || entry?.feature;
      for (const inline of entry?.inlineFeatures || []) entry.node.append(inline);
      const hasContent = feature && (feature.textContent.trim() || feature.querySelector('img,video,iframe,form,button,a[href]'));
      const wide = matchMedia('(min-width:990px)').matches && feature?.dataset.navFeaturePlacement !== 'inline';
      this.featureStage.hidden = !wide || !hasContent;
      if (!feature) return;
      if (wide && hasContent) {this.featureStage.append(feature); this.featureStage.scrollTop = 0;}
      else if (this.previewFeature) entry.node.append(feature);
      else if (entry.featurePlaceholder.parentNode) entry.featurePlaceholder.after(feature);
    }
    replaceMenuContent(html,menuId='') {
      if(this.dialog.open && (this.current!==this.rootId || this.root.contains(document.activeElement)))return false;
      for(const [feature,home] of this.featureHomes || [])if(home.parentNode)home.replaceWith(feature);
      for(const entry of this.panes.values())if(entry.node!==this.root && entry.placeholder.parentNode)entry.placeholder.replaceWith(entry.node);
      const template=document.createElement('template');template.innerHTML=html;
      this.root.querySelector('[data-nav-root-content]').replaceChildren(template.content);
      if(menuId)this.dataset.kitMenuId=menuId;else delete this.dataset.kitMenuId;
      this.panes=new Map();this.current=this.rootId;this.trail=[];
      this.registerPanes();this.registerFeatures();this.show(this.rootId,false);
      if (this.dialog.open && !this.sheet()) this.enterContent(true);
      return true;
    }
    restoreNativeMenu() {
      if(this.dataset.kitMenuId)this.replaceMenuContent(this.nativeMenuHTML);
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null; clearTimeout(this.closeTimer);
      this.clearContentMotion();this.motionObserver?.disconnect();
      this.closest('.ap-nav-owner')?.menuKitCleanup?.();
      for (const [feature,home] of this.featureHomes || []) if (home.parentNode) home.replaceWith(feature);
      for (const entry of this.panes?.values() || []) {
        if (entry.feature && !this.featureHomes?.has(entry.feature) && entry.featurePlaceholder.parentNode) entry.featurePlaceholder.replaceWith(entry.feature);
        if (entry.node !== this.root && entry.placeholder.parentNode) entry.placeholder.replaceWith(entry.node);
      }
      if(this.dataset.kitMenuId){this.root.querySelector('[data-nav-root-content]').innerHTML=this.nativeMenuHTML;delete this.dataset.kitMenuId;}
    }
  });
}
